import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/clients";
import { Search, Download, ScrollText } from "lucide-react";
import { toast } from "sonner";

const ACTION_STYLE = (a) => {
  if (a.includes("delete")) return "bg-red-50 text-red-700 border-red-200";
  if (a.includes("created") || a.includes("accepted")) return "bg-green-50 text-green-700 border-green-200";
  if (a.includes("login") || a.includes("logout")) return "bg-gray-100 text-gray-600 border-gray-200";
  if (a.includes("password")) return "bg-amber-50 text-amber-700 border-amber-200";
  return "bg-blue-50 text-[#002FA7] border-blue-200";
};

export default function Audit() {
  const [logs, setLogs] = useState([]);
  const [actions, setActions] = useState([]);
  const [search, setSearch] = useState("");
  const [action, setAction] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const params = {};
    if (search) params.search = search;
    if (action !== "all") params.action = action;
    const { data } = await api.get("/audit", { params });
    setLogs(data);
    setLoading(false);
  }, [search, action]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { api.get("/audit/actions").then((r) => setActions(r.data)); }, []);

  const exportCsv = async () => {
    try {
      const res = await api.get("/audit/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "audit_log.csv"; a.click();
      URL.revokeObjectURL(url);
      toast.success("Audit log exported");
    } catch { toast.error("Export failed"); }
  };

  return (
    <div className="p-6 lg:p-8" data-testid="audit-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tighter text-gray-900">Audit Log</h1>
          <p className="text-gray-500 text-sm mt-1">{logs.length} activity record{logs.length !== 1 ? "s" : ""}</p>
        </div>
        <button onClick={exportCsv} data-testid="audit-export-button"
          className="flex items-center gap-1.5 bg-[#002FA7] hover:bg-[#002480] text-white px-4 py-2 rounded-sm font-semibold text-sm transition-colors">
          <Download className="w-4 h-4" /> Download CSV
        </button>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input data-testid="audit-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actor, target, details…"
            className="w-full border border-gray-300 rounded-sm pl-9 pr-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]" />
        </div>
        <select data-testid="audit-action-filter" value={action} onChange={(e) => setAction(e.target.value)}
          className="border border-gray-300 rounded-sm px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#002FA7]">
          <option value="all">All actions</option>
          {actions.map((a) => <option key={a} value={a}>{a}</option>)}
        </select>
      </div>

      <div className="bg-white border border-gray-200 rounded-sm overflow-hidden shadow-sm">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-200">
              {["When", "Who", "Action", "Target", "Details"].map((h) => (
                <th key={h} className="text-xs font-bold uppercase tracking-wider text-gray-500 py-3 px-4">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody data-testid="audit-table-body">
            {loading ? (
              <tr><td colSpan={5} className="py-12 text-center text-sm text-gray-400">Loading…</td></tr>
            ) : logs.length === 0 ? (
              <tr><td colSpan={5} className="py-16 text-center">
                <ScrollText className="w-10 h-10 text-gray-300 mx-auto mb-3" strokeWidth={1.5} />
                <p className="text-sm text-gray-500 font-medium">No activity yet</p>
              </td></tr>
            ) : logs.map((l) => (
              <tr key={l.id} className="border-b border-gray-100 last:border-0 hover:bg-gray-50" data-testid={`audit-row-${l.id}`}>
                <td className="py-3 px-4 text-sm text-gray-500 whitespace-nowrap">{formatDateTime(l.created_at)}</td>
                <td className="py-3 px-4">
                  <p className="text-sm font-semibold text-gray-900">{l.actor_name}</p>
                  <p className="text-xs text-gray-400">{l.actor_email || "—"}</p>
                </td>
                <td className="py-3 px-4"><span className={`text-xs font-semibold px-2 py-0.5 rounded-sm border ${ACTION_STYLE(l.action)}`}>{l.action}</span></td>
                <td className="py-3 px-4 text-sm text-gray-700">{l.target_name || "—"} {l.target_type && <span className="text-xs text-gray-400">({l.target_type})</span>}</td>
                <td className="py-3 px-4 text-sm text-gray-500">{l.details || "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
